import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, useModel } from '@umijs/max';
import React from 'react';
import { Avatar, Space, Typography } from 'antd';
import {
  AvatarDropdown,
  Footer,
} from '@/components';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { assetUrl, clearShareBackgroundAssets, iconPublicUrl, isSharePagePath, syncGlobalBackgroundAssets } from '@/utils/assetUrl';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const API_ORIGIN = 'https://api.sc9.cn';

const isLoginPath = (pathname: string) =>
  pathname === loginPath || pathname.endsWith('/user/login');

const isSharePath = (pathname: string) => isSharePagePath(pathname);

const syncPageBodyClass = (pathname: string) => {
  if (typeof document === 'undefined') return;
  const onShare = isSharePath(pathname);
  document.documentElement.classList.toggle('geo-share-page', onShare);
  document.body.classList.toggle('geo-login-page', isLoginPath(pathname));
  document.body.classList.toggle('geo-share-page', onShare);
  if (onShare) {
    clearShareBackgroundAssets();
  } else {
    syncGlobalBackgroundAssets();
  }
};

/** 用户未上传头像时的默认图（public/icons/default_ico.png） */
const defaultUserAvatar = () => assetUrl('icons/default_ico.png');

/** 侧栏/顶栏左上角默认品牌图（未配置 site_logo 时使用，public/icons/banner_left.png） */
const sidebarBrandImage = () => assetUrl('icons/banner_left.png');

/** 从 localStorage（与登录页 getSiteSetting / 登录接口写入一致）读取自定义 Logo */
const getStoredSiteLogo = (): string => {
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem('siteSetting') : null;
    if (!raw) return '';
    const o = JSON.parse(raw) as { site_logo?: string };
    return String(o?.site_logo || '').trim();
  } catch {
    return '';
  }
};

const resolveSidebarBrandSrc = (): string => {
  const custom = getStoredSiteLogo();
  if (custom) return withApiOrigin(custom);
  return sidebarBrandImage();
};

/** 与 Pro 默认顶栏高度一致（token.layout.header.heightLayoutHeader 默认 56） */
const LAYOUT_HEADER_HEIGHT = 56;

const withApiOrigin = (url?: string) => {
  const val = String(url || '').trim();
  if (!val) return '';
  if (/^https?:\/\//i.test(val)) return val;
  return `${API_ORIGIN}${val.startsWith('/') ? '' : '/'}${val}`;
};

/** 有头像则走 CDN/API 域名拼接；为空则用本地默认图 */
const resolveUserAvatarSrc = (url?: string) => {
  const val = String(url || '').trim();
  if (!val) return defaultUserAvatar();
  return withApiOrigin(val);
};

const getStoredAvatar = () => {
  try {
    const userInfoStr = sessionStorage.getItem('userInfo') || localStorage.getItem('userInfo');
    if (!userInfoStr) return '';
    const userInfo = JSON.parse(userInfoStr);
    return String(userInfo?.avatar || '').trim();
  } catch (e) {
    return '';
  }
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      // 新窗口代登录使用 sessionStorage，普通登录使用 localStorage
      const userInfoStr = sessionStorage.getItem('userInfo') || localStorage.getItem('userInfo');
      if (userInfoStr) {
        return JSON.parse(userInfoStr);
      }
    } catch (error) {
      console.log('获取用户信息失败:', error);
    }
    return undefined;
  };
  
  // 获取用户信息
  const currentUser = await fetchUserInfo();
  
  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
}) => {
  return {
    actionsRender: () => [],
    avatarProps: {
      src: resolveUserAvatarSrc((initialState?.currentUser as any)?.avatar || getStoredAvatar()),
      render: (_, avatarChildren) => {
        const user: any = initialState?.currentUser || {};
        const name = user?.company_name || user?.name || user?.username || '用户';
        const avatarSrc = resolveUserAvatarSrc(user?.avatar || getStoredAvatar());
        const userType = Number(user?.user_type || user?.role || 0);
        const roleText =
          userType === 1 ? '超管' :
          userType === 2 ? 'OEM' :
          userType === 3 ? '代理' :
          userType === 4 ? '用户' : '用户';

        return (
          <AvatarDropdown>
            <Space size={10} style={{ cursor: 'pointer' }}>
              <Avatar src={avatarSrc} size={32} />
              <div style={{ lineHeight: 1.2 }}>
                <Typography.Text strong style={{ display: 'block' }}>
                  {name}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                  {roleText}
                </Typography.Text>
              </div>
            </Space>
          </AvatarDropdown>
        );
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      syncPageBodyClass(location.pathname);
      // 如果没有登录且不是登录页面，重定向到 login
      if (!initialState?.currentUser &&
          !isLoginPath(location.pathname) &&
          !location.pathname.startsWith('/share')) {
        history.push(loginPath);
      }
    },
    // 403 页面处理
    unAccessible: <div style={{ textAlign: 'center', padding: '50px' }}>无权访问</div>,
    bgLayoutImgList: [],
    logo: false,
    links: [],
    // 自定义品牌 logo，只在顶部显示图片
    menuHeaderRender: undefined,
    headerTitleRender: (_, __, props) => {
      if (props?.collapsed) {
        return null;
      }
      /** 与侧栏同宽，避免超大 banner 图按固有像素把顶栏 flex 撑爆 */
      const brandW = Number((props as { siderWidth?: number })?.siderWidth) || 215;
      return (
        <div
          className="geo-sidebar-brand-wrap"
          style={{
            width: brandW,
            maxWidth: '100%',
            height: LAYOUT_HEADER_HEIGHT,
            maxHeight: LAYOUT_HEADER_HEIGHT,
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            padding: 0,
            margin: 0,
            overflow: 'hidden',
            boxSizing: 'border-box',
            lineHeight: 0,
          }}
        >
          <img
            src={resolveSidebarBrandSrc()}
            alt="站点品牌"
            style={{
              width: '100%',
              height: '100%',
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              display: 'block',
              margin: 0,
              padding: 0,
            }}
          />
        </div>
      );
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      const { pathname } = history.location;
      syncPageBodyClass(pathname);
      if (isLoginPath(pathname) || isSharePagePath(pathname)) {
        return <>{children}</>;
      }
      return (
        <div
          className="geo-app-page-bg"
          style={{
            minHeight: '100vh',
            backgroundImage: `url("${iconPublicUrl('web_pic.png')}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
          }}
        >
          {children}
        </div>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
/** 首屏渲染前注入背景图路径 */
export function render(oldRender: () => void) {
  if (typeof window !== 'undefined') {
    syncPageBodyClass(window.location.pathname);
  } else {
    syncGlobalBackgroundAssets();
  }
  oldRender();
}

/** 路由切换时同步页面标记（登录页/分享页不加载 web_pic 背景） */
export function onRouteChange({ location }: { location: { pathname: string } }) {
  syncPageBodyClass(location.pathname);
}

export const request: RequestConfig = {
  // 开发环境使用代理，生产环境使用API域名
  baseURL: process.env.NODE_ENV === 'production' ? 'https://api.sc9.cn' : '',
  ...errorConfig,
};
